import { SelectV2, SelectV2Option } from '@cimplico/components';
import { Ref, useEffect, useState } from 'react';
import { EntityTypeEnum } from '../../types/entity';
import { formatMixedCase } from '../../utilities/formatting';

type EntityTypeSelectProps = {
    defaultEntityType?: EntityTypeEnum[];
    title?: string;
    hintMessage?: string;
    isOptional?: boolean;
    isMulti?: boolean;
    placeholder?: string;
    form?: string;
    errorMessage?: string;
    isDisabled?: boolean;
    onChange: (entityTypes: EntityTypeEnum[]) => void;
    innerRef?: Ref<HTMLInputElement>;
    size?: 'small' | 'standard';
};

function EntityTypeSelect({
    defaultEntityType = [],
    title,
    hintMessage,
    isMulti = false,
    isOptional = false,
    placeholder,
    form,
    errorMessage,
    isDisabled,
    onChange,
    innerRef,
    size,
}: EntityTypeSelectProps): JSX.Element {
    const [selected, setSelected] = useState<SelectV2Option[]>(
        defaultEntityType.map((value) => {
            return { value: value, label: formatMixedCase(value) };
        })
    );

    const [search, setSearch] = useState('');

    function handleEntityTypeChange(options: SelectV2Option[]): void {
        onChange(options.map((o) => o.value as EntityTypeEnum));
    }

    const options: { [key: string]: EntityTypeEnum } = {
        Company: EntityTypeEnum.COMPANY,
        Trust: EntityTypeEnum.TRUST,
        Partnership: EntityTypeEnum.PARTNERSHIP,
        'Sole Trader': EntityTypeEnum.SOLE_TRADER,
        Individual: EntityTypeEnum.INDIVIDUAL,
        'Unit Trust': EntityTypeEnum.UNIT_TRUST,
        Estate: EntityTypeEnum.ESTATE,
        'Self Managed Superannuation Fund': EntityTypeEnum.SELF_MANAGED_SUPERANNUATION_FUND,
        'Superannuation Fund': EntityTypeEnum.SUPERANNUATION_FUND,
        'Government Entity': EntityTypeEnum.GOVERNMENT_ENTITY,
        'Club Or Society': EntityTypeEnum.CLUB_OR_SOCIETY,
        'Not For Profit': EntityTypeEnum.NOT_FOR_PROFIT,
        'Accounting Practice': EntityTypeEnum.ACCOUNTING_PRACTICE,
        Charity: EntityTypeEnum.CHARITY,
        'Look Through Company': EntityTypeEnum.LOOK_THROUGH_COMPANY,
        'S Corporation': EntityTypeEnum.S_CORPORATION,
        Other: EntityTypeEnum.OTHER,
    };

    useEffect(() => {
        if (defaultEntityType.length === 0) {
            setSelected([]);
        } else {
            const defaultSelected: SelectV2Option[] = defaultEntityType.map((value) => {
                return { value: value, label: formatMixedCase(value) };
            });
            setSelected(defaultSelected);
        }
    }, [defaultEntityType]);

    return (
        <div>
            <SelectV2
                keepSelectedOptions
                placeholder={placeholder || 'Select entity type...'}
                title={title}
                form={form}
                isOptional={isOptional}
                isMulti={isMulti}
                errorMessage={errorMessage}
                value={selected}
                onChange={handleEntityTypeChange}
                onSearch={setSearch}
                options={Object.keys(options)
                    .filter((opt) => opt.toLowerCase().includes(search.toLowerCase()))
                    .sort((a, b) => (a > b ? 1 : -1))
                    .map((option) => {
                        return { value: options[option], label: option };
                    })}
                disabled={isDisabled}
                ref={innerRef}
                hint={hintMessage}
                size={size}
            />
        </div>
    );
}
export default EntityTypeSelect;
